import { useState } from 'react';
import { CheckCircle2, Loader2, Mail } from 'lucide-react';
import { BrandNav, SiteFooter } from '../components/SiteChrome';
import { SP_EMAIL, SP_MAILTO } from '../lib/brand';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const inputCls = 'w-full bg-black border border-zinc-800 rounded-md px-3 py-2.5 text-sm text-white placeholder-zinc-600 focus:outline-none focus:border-brand/60 transition-colors';
const labelCls = 'block text-[11px] font-mono uppercase tracking-wider text-zinc-500 mb-1.5';
const EMPTY = { name: '', email: '', company: '', message: '', website: '' };

const getSource = () => {
  const q = new URLSearchParams(window.location.search);
  const viaCard = sessionStorage.getItem('tpmi_via_card');
  if (viaCard !== null) return { source: 'shared_card', source_ticker: viaCard };
  return { source: q.get('from') || 'direct', source_ticker: q.get('t') || '' };
};

export default function ContactPage() {
  const [form, setForm] = useState(EMPTY);
  const [status, setStatus] = useState('idle');
  const [error, setError] = useState(null);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const valid = form.name.trim() && /\S+@\S+\.\S+/.test(form.email) && form.message.trim().length >= 5;

  const submit = async (e) => {
    e.preventDefault();
    if (!valid) return;
    setStatus('sending'); setError(null);
    try {
      const res = await fetch(`${API_URL}/api/enquiries`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ ...form, ...getSource() }),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(typeof d.detail === 'string' ? d.detail : 'Please check your details and try again.');
      setStatus('sent'); setForm(EMPTY);
    } catch (err) { setError(err.message); setStatus('idle'); }
  };

  return (
    <div className="min-h-screen bg-black flex flex-col">
      <BrandNav width="max-w-4xl" />
      <main className="flex-1 max-w-xl mx-auto px-4 py-10 w-full">
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight mb-2" data-testid="contact-title">Work with Standards Protocol</h1>
        <p className="text-sm text-zinc-500 mb-6">Tailored market-intelligence solutions, white-label platforms and partnerships. Tell us what you need and the team will reply.</p>

        {status === 'sent' ? (
          <div className="bg-zinc-950 border border-brand/40 rounded-lg p-6 text-center" data-testid="enquiry-success">
            <CheckCircle2 className="w-8 h-8 text-brand mx-auto mb-3" />
            <p className="font-bold mb-1">Thanks, your enquiry has been received.</p>
            <p className="text-xs text-zinc-500 mb-4">The Standards Protocol team will be in touch.</p>
            <button onClick={() => setStatus('idle')} className="text-xs text-zinc-400 hover:text-white underline" data-testid="enquiry-another-btn">Send another enquiry</button>
          </div>
        ) : (
          <form onSubmit={submit} className="bg-zinc-950 border border-zinc-800 rounded-lg p-5 space-y-4" data-testid="enquiry-form" noValidate>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div><label className={labelCls}>Name *</label><input value={form.name} onChange={set('name')} maxLength={100} className={inputCls} data-testid="enquiry-name-input" /></div>
              <div><label className={labelCls}>Email *</label><input type="email" value={form.email} onChange={set('email')} maxLength={200} className={inputCls} data-testid="enquiry-email-input" /></div>
            </div>
            <div><label className={labelCls}>Company (optional)</label><input value={form.company} onChange={set('company')} maxLength={120} className={inputCls} data-testid="enquiry-company-input" /></div>
            <div><label className={labelCls}>Message *</label><textarea value={form.message} onChange={set('message')} rows={5} maxLength={2000} className={`${inputCls} resize-y`} data-testid="enquiry-message-input" /></div>
            <input type="text" value={form.website} onChange={set('website')} tabIndex={-1} autoComplete="off" aria-hidden="true" className="hidden" />
            {error && <p className="text-xs text-red-400" data-testid="enquiry-error">{error}</p>}
            <button type="submit" disabled={!valid || status === 'sending'} data-testid="enquiry-submit-btn"
              className="w-full bg-brand hover:bg-brand-light text-black font-bold text-sm py-3 rounded-md transition-colors disabled:opacity-30 disabled:cursor-not-allowed flex items-center justify-center gap-2">
              {status === 'sending' ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Send enquiry'}
            </button>
          </form>
        )}
        <p className="text-xs text-zinc-500 mt-5 flex items-center gap-1.5">
          <Mail className="w-3.5 h-3.5" /> Prefer email? <a href={SP_MAILTO} className="text-brand hover:underline" data-testid="contact-mailto-link">{SP_EMAIL}</a>
        </p>
      </main>
      <SiteFooter />
    </div>
  );
}
